import { FC, useState } from 'react';
import type { SeopProductionAnalysis, SeopProductionRow } from '@/types/seop';
import { seopService } from '@/services/seop.service';
import { fmtMarginPct, fmtMoneyBRL, fmtUnits, seopNum } from '@/utils/seopFormat';

const CELL = 'py-2 pr-3 text-sm text-content-primary';
const NUM = `${CELL} text-right tabular-nums`;

const dataBR = (iso: string | null): string =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('pt-BR') : '—';

/** Verde quando sobra, vermelho quando falta, neutro no zero — como na planilha dele. */
const tomDaDiferenca = (valor: number): string => {
  if (valor > 0) return 'text-theme-success';
  if (valor < 0) return 'text-theme-danger';
  return 'text-content-muted';
};

const Diferenca: FC<{ valor: number; formatar: (v: number) => string }> = ({ valor, formatar }) => (
  <span className={tomDaDiferenca(valor)}>{formatar(valor)}</span>
);

const LinhaDeProducao: FC<{ linha: SeopProductionRow; total?: boolean }> = ({ linha, total = false }) => {
  const pct = linha.qty_difference_pct === null ? null : seopNum(linha.qty_difference_pct);
  const peso = total ? 'font-semibold' : '';
  return (
    <tr className={`border-b border-theme-border/50 ${total ? 'bg-surface-secondary/40' : ''}`}>
      <td className={`${CELL} ${peso}`}>{total ? 'TOTAL' : linha.code}</td>
      <td className={`${CELL} max-w-xs truncate`} title={linha.description}>
        {linha.description}
      </td>
      <td className={`${NUM} ${peso}`}>{fmtUnits(seopNum(linha.planned_qty))}</td>
      <td className={`${NUM} ${peso}`}>{fmtUnits(seopNum(linha.real_qty))}</td>
      <td className={NUM}>
        <Diferenca valor={seopNum(linha.qty_difference)} formatar={fmtUnits} />
      </td>
      <td className={NUM}>
        {pct === null ? <span className="text-content-muted">—</span> : <Diferenca valor={pct} formatar={fmtMarginPct} />}
      </td>
      <td className={NUM}>{fmtMoneyBRL(seopNum(linha.pmv_planned))}</td>
      <td className={NUM}>{fmtMoneyBRL(seopNum(linha.pmv_real))}</td>
      <td className={NUM}>
        <Diferenca valor={seopNum(linha.pmv_difference)} formatar={fmtMoneyBRL} />
      </td>
      <td className={NUM}>{fmtMoneyBRL(seopNum(linha.margin_planned))}</td>
      <td className={NUM}>{fmtMoneyBRL(seopNum(linha.margin_real))}</td>
      <td className={NUM}>
        <Diferenca valor={seopNum(linha.margin_difference)} formatar={fmtMoneyBRL} />
      </td>
    </tr>
  );
};

const TabelaDeProducao: FC<{ dados: SeopProductionAnalysis }> = ({ dados }) => (
  <div className="mt-3 overflow-x-auto">
    <table data-testid="seop-tabela-producao" className="w-full">
      <thead>
        <tr className="border-b border-theme-border text-xs uppercase tracking-wide text-content-muted">
          <th className="pb-2 pr-3 text-left">Item</th>
          <th className="pb-2 pr-3 text-left">Descrição</th>
          <th className="pb-2 pr-3 text-right">Produção planejada</th>
          <th className="pb-2 pr-3 text-right">Produção real</th>
          <th className="pb-2 pr-3 text-right">Diferença</th>
          <th className="pb-2 pr-3 text-right">%</th>
          <th className="pb-2 pr-3 text-right">R$ PMV planejado</th>
          <th className="pb-2 pr-3 text-right">R$ PMV real</th>
          <th className="pb-2 pr-3 text-right">Dif. PMV</th>
          <th className="pb-2 pr-3 text-right">R$ margem planejada</th>
          <th className="pb-2 pr-3 text-right">R$ margem real</th>
          <th className="pb-2 pr-3 text-right">Dif. margem</th>
        </tr>
      </thead>
      <tbody>
        {dados.rows.map((linha) => (
          <LinhaDeProducao key={linha.code} linha={linha} />
        ))}
        <LinhaDeProducao linha={dados.total} total />
      </tbody>
    </table>
  </div>
);

interface AnaliseDeProducaoProps {
  month: number;
}

/**
 * Plano × produção real do período, para a reunião de fechamento.
 *
 * Carrega sob demanda, e não junto com a tela: a consulta vai à réplica do Datasul, que
 * é compartilhada com o Oráculo e com a TI. O rótulo do período e o "dados até" ficam
 * sempre à vista — em mês aberto o realizado é parcial de propósito, e ler a diferença
 * sem essa data leva a concluir que a fábrica produziu menos do que produziu.
 */
const AnaliseDeProducao: FC<AnaliseDeProducaoProps> = ({ month }) => {
  const [dados, setDados] = useState<SeopProductionAnalysis | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);

  const carregar = async () => {
    setCarregando(true);
    setErro(null);
    try {
      setDados(await seopService.getProductionAnalysis(month));
    } catch (e) {
      const resposta = (e as { response?: { data?: { message?: string } } })?.response?.data;
      setErro(resposta?.message || 'Não consegui ler a produção real agora.');
      setDados(null);
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-theme-border bg-surface-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-content-primary">Análise de produção</h3>
          <p className="text-xs text-content-muted">
            Compara o plano do período com o produzido reportado nas OPs (o CP0511 sem reprocesso), lido do Datasul na hora.
          </p>
        </div>
        <button
          type="button"
          data-testid="seop-analise-producao"
          onClick={carregar}
          disabled={carregando}
          className="rounded-lg border border-theme-border px-4 py-2 text-sm text-content-secondary hover:text-content-primary disabled:opacity-50"
        >
          {carregando ? 'Consultando…' : dados ? 'Atualizar' : 'Carregar análise'}
        </button>
      </div>

      {erro && (
        <p data-testid="seop-erro-producao" className="mt-3 text-sm text-theme-danger">
          {erro}
        </p>
      )}

      {dados && (
        <>
          <p className="mt-3 text-xs text-content-muted">
            Período de <strong>{dataBR(dados.period.start)}</strong> a{' '}
            <strong>{dataBR(dados.period.end)}</strong> · produção com dados até{' '}
            <strong>{dataBR(dados.produced_until)}</strong>.
          </p>
          <TabelaDeProducao dados={dados} />
          {dados.out_of_plan.length > 0 && (
            <p data-testid="seop-fora-do-plano" className="mt-2 text-xs text-content-muted">
              Produzido fora do plano:{' '}
              {dados.out_of_plan
                .map((x) => `${x.code} (${fmtUnits(seopNum(x.real_qty))})`)
                .join(' · ')}
              .
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default AnaliseDeProducao;
